import { useQuery } from "@apollo/client";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { useMemo, useState, useRef } from "react";
import { ChartWrapper } from "../../../../components/ChartWrapper";
import { ClusteredColumnChart } from "../../../../components/charts/ClusteredColumnChart/ClusteredColumnChart";
import { ClusteredColumnChartDataItem } from "../../../../components/charts/ClusteredColumnChart/types";
import {
  DEFAULT_GROUP_BY,
  DEFAULT_ORDER_BY,
  EMPLOYEES_RANGE,
  REVENUE_RANGE,
} from "../MarketLandscape/charts/KeyPlayerMarketPenetrationBySegment/constants";
import { TECHNOLOGY_MARKET_SHARE_QUERY } from "../MarketLandscape/charts/KeyPlayerMarketPenetrationBySegment/queries";
import {
  TechnologyMarketSharePayload,
  TechnologyMarketShareVariables,
} from "../MarketLandscape/charts/KeyPlayerMarketPenetrationBySegment/types";
import { processChartData } from "../MarketLandscape/charts/KeyPlayerMarketPenetrationBySegment/utils";
import { DetectionSegment } from "../MarketLandscape/hooks/useProductDetectionsBySegment";
import { getTop5CompetitorsByPenetration } from "./utils";
import { BarChartColors } from "../../../../components/charts/constants";
import useMatchProductsById from "../MarketLandscape/hooks/useMatchProductsById";
import { COMPANY_SEARCH_CLIENT } from "../../../../utils/consts";
import { useWorkspaceStore } from "../../../../store/workspace/WorkspaceStore";
import {
  determineChartContent,
  generateChartSegmentNamesForCSV,
  hasNoData,
} from "../../utils";
import { useCombinedFilters } from "../../../../hooks/useCombinedFilters";
import * as am4charts from "@amcharts/amcharts4/charts";
import { CHART_CONSTANTS } from "../constants";

const { baseId, tooltip, chartTitle } =
  CHART_CONSTANTS.competitorMarketPenetrationBySegment;

export function CompetitorMarketPenetrationBySegmentChart() {
  const chartRef = useRef<am4charts.XYChart>(null);
  const [selectedSegment, setSelectedSegment] = useState(
    DetectionSegment.COUNTRY
  );
  const combinedFilters = useCombinedFilters();
  const competitorsProductIds = useWorkspaceStore(
    (state) => state.draft?.configuration.competitorsProductIds || []
  );
  const localizeInstalls = useWorkspaceStore(
    (state) => state.draft?.configuration?.localizeInstalls ?? true
  );
  const workspaceWhitespace = useWorkspaceStore(
    (state) => state.draft?.configuration?.whitespace
  );

  const { productsNameMapById } = useMatchProductsById(competitorsProductIds);

  const ranges = useMemo(() => {
    if (selectedSegment === DetectionSegment.REVENUE) {
      return REVENUE_RANGE;
    }

    if (selectedSegment === DetectionSegment.EMPLOYEES) {
      return EMPLOYEES_RANGE;
    }
  }, [selectedSegment]);

  const { data: queryData, loading } = useQuery<
    TechnologyMarketSharePayload,
    TechnologyMarketShareVariables
  >(TECHNOLOGY_MARKET_SHARE_QUERY, {
    variables: {
      productIds: competitorsProductIds,
      compareAcross: selectedSegment,
      companyFilters: combinedFilters || {},
      orderBy: DEFAULT_ORDER_BY,
      groupBy: DEFAULT_GROUP_BY,
      ranges,
      localizeInstalls,
      companyMatchFile:
        combinedFilters?.opportunityType &&
        workspaceWhitespace?.fileId &&
        workspaceWhitespace?.lastMatched
          ? workspaceWhitespace
          : undefined,
    },
    fetchPolicy: "no-cache",
    skip: competitorsProductIds.length === 0,
    context: {
      clientName: COMPANY_SEARCH_CLIENT,
    },
  });

  const top5CompetitorsData = useMemo(() => {
    return getTop5CompetitorsByPenetration(queryData?.technologyMarketShare);
  }, [queryData?.technologyMarketShare]);

  const chartData = useMemo<ClusteredColumnChartDataItem[]>(() => {
    return processChartData(
      top5CompetitorsData,
      selectedSegment,
      productsNameMapById
    );
  }, [productsNameMapById, top5CompetitorsData, selectedSegment]);

  const noDataReturned = useMemo(
    () => hasNoData(loading, chartData),
    [chartData, loading]
  );

  const chartContent = useMemo(() => {
    return determineChartContent({
      productIds: competitorsProductIds,
      isLoading: loading,
      noDataReturned,
      baseId,
      chartToRender: (
        <ClusteredColumnChart
          id={`${baseId}-chart`}
          chartData={chartData}
          baseColor={BarChartColors}
          yAxisLabel="Market Penetration (%)"
          showPercentageSymbol
          ref={chartRef}
          showCustomTooltip
        />
      ),
    });
  }, [chartData, loading, noDataReturned, competitorsProductIds]);

  const csvFieldMappings = useMemo(() => {
    const fieldMappings: Record<string, string> = {
      category: generateChartSegmentNamesForCSV(selectedSegment),
    };
    if (chartData.length > 0) {
      const uniqueProducts = new Set<string>();
      chartData.forEach((item) => {
        item.cluster.forEach((product) => {
          uniqueProducts.add(product.name);
        });
      });

      uniqueProducts.forEach((productName) => {
        fieldMappings[productName] = `${productName} (% Market Penetration)`;
        fieldMappings[`${productName}_count`] =
          `${productName} (# of Companies)`;
      });
    }

    return fieldMappings;
  }, [chartData, selectedSegment]);

  function handleSegmentSelect(event: SelectChangeEvent) {
    setSelectedSegment(event.target.value as DetectionSegment);
  }

  return (
    <ChartWrapper
      id={`${baseId}-chart-wrapper`}
      title={chartTitle}
      tooltipTitle={tooltip}
      chart={chartContent}
      noData={noDataReturned || competitorsProductIds.length === 0}
      chartRef={chartRef}
      isLoading={loading}
      titleExtras={
        <FormControl size="small" sx={{ minWidth: 180, maxWidth: "100%" }}>
          <Select
            id={`${baseId}-select`}
            value={selectedSegment}
            onChange={handleSegmentSelect}
          >
            <MenuItem value={`${DetectionSegment.COUNTRY}`}>
              Segment: Country
            </MenuItem>
            <MenuItem value={`${DetectionSegment.REGION}`}>
              Segment: Region
            </MenuItem>
            <MenuItem value={`${DetectionSegment.HG_INDUSTRY}`}>
              Segment: HG Industry
            </MenuItem>
            {/* the query expects INDUSTRY as the segment name for naics data*/}
            <MenuItem value={`${DetectionSegment.INDUSTRY}`}>
              Segment: NAICS
            </MenuItem>
            <MenuItem value={`${DetectionSegment.EMPLOYEES}`}>
              Segment: Employee Range
            </MenuItem>
            <MenuItem value={`${DetectionSegment.REVENUE}`}>
              Segment: Revenue Range
            </MenuItem>
          </Select>
        </FormControl>
      }
      csvFieldMappings={csvFieldMappings}
    />
  );
}
